/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, Dispatch, SetStateAction, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';

import { Box } from '@mui/material';

import { getColor } from '../../common/UICommon';

import creditCard from '../../../assets/creditCard.svg';
import { useAnalytics } from '../../../utils/analytics';
import { TabletWidth } from '../../../global.style';

const CardFormWrapper = styled(Box)`
  font-weight: 600;
  line-height: 20px;
  color: ${getColor('BLACK')};
  margin-bottom: 32px;

  @media (max-width: ${TabletWidth}px) {
    margin-bottom: 0px;
  }
`;

const Label = styled.p`
  margin-bottom: 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Date = styled(Box)`
  display: flex;
  margin-top: 32px;
  & div {
    flex-grow: 1;
  }
`;

const CardDiv = styled.div`
  border-radius: 4px;
  padding-top: 16px;
  background-color: ${getColor('WHITE')};
  height: 48px;
  &.creditCard {
    background-image: url(${creditCard});
    background-repeat: no-repeat;
    background-position: 12px 10px;
    padding-left: 50px;
  }
  &.other {
    padding-left: 16px;
  }
  &.error {
    border: 1px solid ${getColor('RED_100')};
  }
`;

const CardIfo = styled.div`
  width: 50%;
  &.exp {
    margin-right: 16px;
  }
`;

const PaymentButtonContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  line-height: 20px;
  font-size: 14px;
  font-weight: 600;
  color: ${getColor('BLACK')};
  border-radius: 4px;
`;

const ExpressCheckoutContainer = styled(Box)`
  margin-bottom: 32px;
  max-width: 528px;
  @media (max-width: ${TabletWidth}px) {
    flex-direction: row;
    padding: 0px;
    margin: 0px;
    margin-bottom: 32px;
    padding-bottom: 32px;
    border-bottom: 1px solid ${getColor('GRAY_30')};
  }
`;
const PaymentButtonWrapper = styled(Box)`
  width: 100%;
`;
export enum CardInputType {
  Card = 'Card',
  Cvv = 'Cvv',
  Date = 'Date',
}

const getOptions = (variant: CardInputType) => {
  const options = {
    style: {
      base: {
        fontSize: '16px',
        fontWeight: '400',
        backgroundColor: getColor('WHITE'),
        '::placeholder': {
          color: getColor('BLACK_60'),
        },
      },
    },
    placeholder: '',
  };

  if (variant === CardInputType.Card) {
    options.placeholder = '1234 5678 1234 5678';
  }

  if (variant === CardInputType.Cvv) {
    options.placeholder = '3-4 digits';
  }

  if (variant === CardInputType.Date) {
    options.placeholder = 'MM / YY';
  }

  return options;
};

export const CardForm = ({
  setIsCardInfoCorrect,
  paymentRequest,
}: {
  setIsCardInfoCorrect: (value: boolean) => void;
  paymentRequest: PaymentRequest | null;
}) => {
  const analytics = useAnalytics();

  const [cardNumberState, setCardNumberState] = useState({
    state: 'unknown',
    className: '',
  });
  const [cardExpState, setCardExpState] = useState({
    state: 'unknown',
    className: '',
  });
  const [cardCvcState, setCardCvcState] = useState({
    state: 'unknown',
    className: '',
  });

  useEffect(() => {
    if (cardNumberState.state === 'valid' && cardExpState.state === 'valid' && cardCvcState.state === 'valid') {
      setIsCardInfoCorrect(true);
    } else {
      setIsCardInfoCorrect(false);
    }
  }, [cardNumberState, cardExpState, cardCvcState]);

  const handleChange = (
    currentState: string,
    setMethod: Dispatch<SetStateAction<{ state: string; className: string }>>,
    complete: boolean,
    error: boolean
  ) => {
    if (complete && currentState !== 'valid') {
      return setMethod({ state: 'valid', className: '' });
    }
    if (currentState !== 'unknown') {
      setMethod({ state: 'unknown', className: '' });
    }
    if (error && currentState !== 'invalid') {
      return setMethod({ state: 'invalid', className: 'error' });
    }
  };

  const onAppleGooglePaymentRequest = () => {
    analytics.track('pay with google apple pay click');
  };

  return (
    <CardFormWrapper>
      {paymentRequest && (
        <ExpressCheckoutContainer>
          <Label>Express checkout</Label>
          <PaymentButtonContainer>
            <PaymentButtonWrapper>
              <PaymentRequestButtonElement options={{ paymentRequest }} onClick={onAppleGooglePaymentRequest} />
            </PaymentButtonWrapper>
          </PaymentButtonContainer>
        </ExpressCheckoutContainer>
      )}

      <Label>Pay with credit card</Label>
      <CardDiv className={`creditCard ${cardNumberState.className}`}>
        <CardNumberElement
          options={getOptions(CardInputType.Card)}
          onChange={(event) => {
            const { complete, error } = event;
            handleChange(cardNumberState.state, setCardNumberState, complete, !!error);
          }}
        />
      </CardDiv>

      <Date>
        <CardIfo className="exp">
          <Label>Expiration Date</Label>
          <CardDiv className={`other ${cardExpState.className}`}>
            <CardExpiryElement
              options={getOptions(CardInputType.Date)}
              onChange={(event) => {
                const { complete, error } = event;
                handleChange(cardExpState.state, setCardExpState, complete, !!error);
              }}
            />
          </CardDiv>
        </CardIfo>

        <CardIfo>
          <Label>CVV</Label>
          <CardDiv className={`other ${cardCvcState.className}`}>
            <CardCvcElement
              options={getOptions(CardInputType.Cvv)}
              onChange={(event) => {
                const { complete, error } = event;
                handleChange(cardCvcState.state, setCardCvcState, complete, !!error);
              }}
            />
          </CardDiv>
        </CardIfo>
      </Date>
    </CardFormWrapper>
  );
};
